import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PORTRAIT = "/img/jess-portrait.jpg";

const Become = () => (
  <main className="bg-[#F6F1EA]" data-testid="become-page">
    {/* Hero */}
    <section className="pt-28 pb-14 sm:pt-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
        <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-6">Stage 03 · A 12-session partnership</p>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[#4F5D4F] leading-[1.05] tracking-tight uppercase" data-testid="become-heading">
          Become
        </h1>
        <p className="mt-6 font-serif italic text-2xl sm:text-3xl text-[#4F5D4F] leading-snug">Becoming Her.</p>
        <p className="mt-2 font-serif italic text-lg text-[#B89B63]">The woman you were always meant to be.</p>
        <p className="mt-6 text-base sm:text-lg text-[#4F5D4F]/85 font-light max-w-xl mx-auto">
          A deeply personal 12-session coaching experience — typically spanning six months — for
          women rebuilding their faith, identity, and trust in themselves after high-control
          Christianity.
        </p>
      </div>
    </section>

    {/* Copy */}
    <section className="pb-14">
      <div className="max-w-2xl mx-auto px-6 space-y-5 text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-[1.75]">
        <p className="font-serif italic text-xl text-[#4F5D4F]">
          Twelve conversations. Your questions. Your pace. Your becoming.
        </p>
        <p>There isn't a predetermined version of you we're trying to reach.</p>
        <p>You've spent enough of your life being told who to be.</p>
        <p>
          Over 12 private sessions, we'll make space to untangle what you've inherited, explore
          what you actually believe, reconnect with your own voice, and navigate the decisions,
          relationships, questions, and changes that come with becoming who you are now.
        </p>
        <p className="font-serif italic text-lg text-[#4F5D4F]">
          I won't tell you where you should land. I'll walk beside you while you discover what is true for you.
        </p>

        <p className="pt-4">Together, we'll explore things like:</p>
        <ul className="space-y-1 text-base text-[#4F5D4F]/90 font-light">
          <li>· what you were taught vs. what you actually believe</li>
          <li>· fear, guilt, and inherited expectations</li>
          <li>· identity outside of the roles you were given</li>
          <li>· your needs, values, desires, and boundaries</li>
          <li>· trusting your own thoughts, feelings, and discernment</li>
          <li>· making decisions without constantly looking outside yourself for permission</li>
          <li>· exploring faith with curiosity instead of fear</li>
          <li>· creating a life that reflects who you are now</li>
          <li>· who you are becoming</li>
        </ul>

        <div className="mt-8 border-l-2 border-[#B89B63] pl-5 py-1">
          <p className="font-serif italic text-lg sm:text-xl text-[#4F5D4F] leading-snug">
            I won't tell you what to believe. I'll help you create enough space to discover what
            you believe—and trust yourself as you do.
          </p>
        </div>
      </div>
    </section>

    {/* Transformation */}
    <section className="py-14 sm:py-16 bg-[#D7B2A3]/15">
      <div className="max-w-2xl mx-auto px-6">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-6" data-testid="transformation-title">
          What if this didn't have to feel <em className="italic text-[#B89B63]">so hard?</em>
        </h2>
        <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light mb-4">Imagine being able to...</p>
        <ul className="space-y-2 font-serif italic text-lg text-[#4F5D4F]/90 leading-snug mb-8">
          <li>Ask the hard questions without feeling like you have to find the answer immediately.</li>
          <li>Say "I don't know yet" and actually be okay with that.</li>
          <li>Hear what someone else believes without immediately questioning yourself.</li>
          <li>Know what you think, what you want, and what you need.</li>
          <li>Make a decision without asking everyone else what they think first.</li>
          <li>Explore God and faith without fear being the loudest voice in the room.</li>
          <li>Feel more at peace with who you are—even while you're still figuring things out.</li>
          <li>Look at the woman you're becoming and think, "Yeah. This feels like me."</li>
        </ul>
        <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed">
          You don't need me to give you the answers. Our work is about helping you hear your own
          voice more clearly, trust what you're discovering, and feel confident navigating what
          comes next—even when you don't have everything figured out.
        </p>
      </div>
    </section>

    {/* Investment */}
    <section className="py-14 sm:py-16">
      <div className="max-w-4xl mx-auto px-6">
        <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3 text-center">Investment</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight text-center mb-10">
          Two ways to begin.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Monthly */}
          <div className="bg-white border border-[#D7B2A3]/60 p-8 flex flex-col" data-testid="become-monthly-card">
            <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">Monthly payment plan</p>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="font-serif text-xl text-[#8C9A88] line-through">$399</span>
              <span className="font-serif text-5xl text-[#4F5D4F]">$235</span>
              <span className="text-sm text-[#4F5D4F]/70 font-light">/mo</span>
            </div>
            <p className="text-sm text-[#4F5D4F]/70 font-light mb-6">2 private sessions per month × 6 months · 12 total</p>
            <ul className="space-y-2 text-sm text-[#4F5D4F]/85 font-light mb-8 flex-1">
              <li>· 12 private 50-minute Faith &amp; Identity Coaching sessions</li>
              <li>· Google Meet + Google Calendar sync</li>
              <li>· Self-serve rebooking portal + private reflection space</li>
              <li>· Auto-cancels after 6 months — no lingering charges</li>
            </ul>
            <Link
              to="/booking?package=membership_monthly"
              className="group inline-flex items-center justify-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-6 py-4 text-xs tracking-eyebrow uppercase transition-colors"
              data-testid="become-monthly-cta"
            >
              Begin Become — Monthly <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Pay in full */}
          <div className="bg-[#4F5D4F] text-[#F6F1EA] border border-[#4F5D4F] p-8 flex flex-col" data-testid="become-fullpay-card">
            <p className="text-xs tracking-eyebrow uppercase text-[#D7B2A3] mb-3">Pay in full</p>
            <div className="flex items-baseline gap-3 mb-3">
              <span className="font-serif text-xl text-[#D7B2A3] line-through">$2,400</span>
              <span className="font-serif text-5xl text-[#F6F1EA]">$1,380</span>
            </div>
            <p className="text-sm text-[#F6F1EA]/80 font-light mb-6">12 private sessions · One-time discounted investment</p>
            <p className="text-sm text-[#F6F1EA]/80 font-light italic mb-6">
              Choose the pay-in-full option and receive a discounted rate on the full six-month experience.
            </p>
            <ul className="space-y-2 text-sm text-[#F6F1EA]/85 font-light mb-8 flex-1">
              <li>· 12 private 50-minute Faith &amp; Identity Coaching sessions</li>
              <li>· Google Meet + Google Calendar sync</li>
              <li>· Self-serve rebooking portal + private reflection space</li>
              <li>· One payment, six months of showing up</li>
            </ul>
            <Link
              to="/booking?package=package_12"
              className="group inline-flex items-center justify-center gap-3 bg-[#F6F1EA] text-[#4F5D4F] hover:bg-[#D7B2A3] px-6 py-4 text-xs tracking-eyebrow uppercase transition-colors"
              data-testid="become-fullpay-cta"
            >
              Begin Become — Pay in Full <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* About Jess */}
    <section className="py-14 sm:py-16 border-t border-[#D7B2A3]/30">
      <div className="max-w-3xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-5">
          <div className="relative aspect-[3/4] max-w-[240px] mx-auto md:mx-0">
            <div className="absolute -top-3 -left-3 w-16 h-16 bg-[#D7B2A3]/60 z-0" />
            <img src={PORTRAIT} alt="Jess Eisaman" className="relative z-10 w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-7">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-3">Who's walking beside you</p>
          <h2 className="font-serif text-3xl text-[#4F5D4F] leading-tight tracking-tight mb-5">
            Hi, I'm <em className="italic text-[#B89B63]">Jess</em>.
          </h2>
          <p className="text-base text-[#4F5D4F]/85 font-light leading-relaxed">
            I'm a Faith &amp; Identity Coach, seminary graduate, former Christian counselor, and
            ICF-certified coach. I'm here to ask thoughtful questions, offer perspective and
            tools, create space for what comes up, and walk beside you as you discover what is
            true for you.
          </p>
        </div>
      </div>
    </section>
  </main>
);

export default Become;
